import { useState, Fragment } from 'react'
import { NavLink } from 'react-router-dom'
import {
  FiHome, FiClock, FiUsers, FiCalendar, FiFileText, FiSmartphone, FiBriefcase,
  FiSettings, FiChevronsLeft, FiChevronsRight,
} from 'react-icons/fi'
import { Logo, Avatar, Hr, Pill, Btn } from '../ui'

/*
 * AppShellV2 — layout v2 con sidebar 240px (expandida) / 64px (colapsada).
 *
 * Configurable: pasa props para personalizar la nav, datos de usuario/
 * empresa/plan, banner superior y acciones de usuario. Si no pasas nada,
 * usa los defaults mock (útil para demos visuales tipo /app-v2/dashboard).
 *
 * Cada item de nav puede ser:
 *   { id, icon, label, badge, to }                → NavLink (active automático)
 *   { id, icon, label, badge, onClick }           → button (active vía activeId)
 *   { id, icon, label, adminBadge: true, … }      → muestra badge "ADMIN" para items reservados a admin
 *
 * Props relevantes:
 *   navPrimary, navSecondary  → arrays de items
 *   topBanner                 → JSX que se renderiza arriba del shell (trial / bloqueada)
 *   topActions                → JSX en la cabecera del menú, justo bajo el logo (notificaciones, etc.)
 *   userActions               → JSX en el pie del menú (avatar + dropdown PIN/password/logout).
 *                               Si pasas userActions, sustituye al user row simple.
 *
 * ────────────────────────────────────────────────────────────────────
 * REGLAS ARQUITECTÓNICAS DEL REDISEÑO v2 (aplicar en TODA migración):
 *
 *  1. Contenido centrado con `max-w-7xl mx-auto` dentro del <main>
 *     para no estirarse hasta el borde derecho en monitores anchos.
 *     Lo aplica el shell automáticamente — las páginas no necesitan
 *     declarar max-width propio; sí su padding interno (px-7 py-6).
 *
 *  2. Sidebar fija al hacer scroll vertical: el shell usa h-screen y
 *     overflow-y-auto solo en <main>. La sidebar conserva su altura
 *     y nunca scrollea con el contenido.
 *
 * Patrón estándar de página v2 dentro del shell:
 *
 *   <>
 *     <PageHeader … />          // sin padding extra, ya trae px-7
 *     <div className="px-7 py-6 space-y-6">
 *       …contenido…
 *     </div>
 *   </>
 *
 * El AppLayoutV2 inyecta <Outlet /> directamente — sin wrap con
 * padding — para que el PageHeader pueda alinear visualmente con
 * el cuerpo (mismo px-7).
 * ────────────────────────────────────────────────────────────────────
 */

const DEFAULT_NAV_PRIMARY = [
  { id: 'dashboard', icon: FiHome, label: 'Resumen' },
  { id: 'fichajes', icon: FiClock, label: 'Fichajes', badge: 12 },
  { id: 'empleados', icon: FiUsers, label: 'Empleados' },
  { id: 'ausencias', icon: FiCalendar, label: 'Ausencias', badge: 2 },
  { id: 'informes', icon: FiFileText, label: 'Informes' },
]

const DEFAULT_NAV_SECONDARY = [
  { id: 'empleado', icon: FiSmartphone, label: 'Vista empleado' },
  { id: 'asesoria', icon: FiBriefcase, label: 'Asesoría' },
  { id: 'ajustes', icon: FiSettings, label: 'Ajustes' },
]

function NavItem({ item, active, onItemClick }) {
  const Ico = item.icon

  // Estilos compartidos para mantener un solo punto de truth
  const baseClasses = 'group relative w-full flex items-center gap-2.5 px-3 py-1.5 rounded-soft text-[13px] text-left transition-colors'
  const inactiveClasses = 'text-[var(--ink-2)] hover:bg-[rgba(11,11,15,.04)] hover:text-[var(--ink)]'
  const activeClasses = 'bg-[var(--bg-2)] text-[var(--ink)] font-medium'

  const inner = (isActive) => (
    <>
      {isActive && (
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full ia-grad-bg" aria-hidden="true" />
      )}
      <Ico size={15} className="flex-shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.adminBadge && (
        <span className="text-[9px] font-mono uppercase tracking-wider text-[#7C3AED] bg-[rgba(124,58,237,.10)] px-1.5 py-0.5 rounded">
          ADMIN
        </span>
      )}
      {item.badge != null && (
        <span className="text-[10.5px] font-mono tnum text-[var(--ink-3)]">{item.badge}</span>
      )}
    </>
  )

  // NavLink si el item tiene `to`
  if (item.to) {
    return (
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onItemClick}
        className={({ isActive }) => `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
      >
        {({ isActive }) => inner(isActive)}
      </NavLink>
    )
  }

  // Button si solo tiene onClick / id
  return (
    <button
      type="button"
      onClick={() => { onItemClick?.(); item.onClick?.() }}
      className={`${baseClasses} ${active ? activeClasses : inactiveClasses}`}
    >
      {inner(active)}
    </button>
  )
}

function CollapsedItem({ item, active, onItemClick }) {
  const Ico = item.icon
  const baseClasses = 'relative w-10 h-10 grid place-items-center rounded-soft mx-auto hover:bg-[var(--bg-2)]'
  const colorActive = 'text-[var(--ink)]'
  const colorIdle = 'text-[var(--ink-3)]'

  const inner = (isActive) => (
    <>
      {isActive && <span className="absolute left-[-12px] top-2 bottom-2 w-[3px] rounded-r-pill ia-grad-bg" />}
      <Ico size={16} />
    </>
  )

  if (item.to) {
    return (
      <NavLink
        to={item.to}
        end={item.end}
        title={item.label}
        onClick={onItemClick}
        className={({ isActive }) => `${baseClasses} ${isActive ? colorActive : colorIdle}`}
      >
        {({ isActive }) => inner(isActive)}
      </NavLink>
    )
  }

  return (
    <button
      type="button"
      title={item.label}
      onClick={() => { onItemClick?.(); item.onClick?.() }}
      className={`${baseClasses} ${active ? colorActive : colorIdle}`}
    >
      {inner(active)}
    </button>
  )
}

function SidebarExpanded({
  navPrimary, navSecondary, activeId, onItemClick,
  onCollapse, user, plan, topActions, userActions,
}) {
  return (
    <aside
      className="w-[240px] shrink-0 border-r border-[var(--line)] flex flex-col overflow-y-auto"
      style={{ background: 'var(--bg)' }}
      aria-label="Navegación lateral"
    >
      <div className="px-4 h-14 flex items-center justify-between">
        <Logo size={22} />
        <button
          type="button"
          onClick={onCollapse}
          className="p-1.5 rounded-soft text-[var(--ink-3)] hover:bg-[var(--bg-2)] focus-visible:ring-2 focus-visible:ring-[var(--brand-1)]"
          aria-label="Colapsar menú"
        >
          <FiChevronsLeft size={14} />
        </button>
      </div>

      {topActions && (
        <>
          <div className="px-3 pb-3">{topActions}</div>
          <Hr />
        </>
      )}

      {navPrimary.length > 0 && (
        <div className="px-2 pt-3 pb-1">
          <div className="eyebrow px-3 pb-2">Espacio de trabajo</div>
          <nav className="flex flex-col gap-px">
            {navPrimary.map((it, i) => {
              // Hairline sutil cuando cambia el "tipo" de item (usuario ↔ admin)
              const prev = navPrimary[i - 1]
              const showDivider = prev && !!it.adminBadge !== !!prev.adminBadge
              return (
                <Fragment key={it.id}>
                  {showDivider && <div className="my-2 mx-3 h-px bg-[var(--line)]" aria-hidden="true" />}
                  <NavItem item={it} active={activeId === it.id} onItemClick={onItemClick} />
                </Fragment>
              )
            })}
          </nav>
        </div>
      )}

      {navSecondary.length > 0 && (
        <div className="px-2 pt-4">
          <div className="eyebrow px-3 pb-2">Más</div>
          <nav className="flex flex-col gap-px">
            {navSecondary.map((it, i) => {
              const prev = navSecondary[i - 1]
              const showDivider = prev && !!it.adminBadge !== !!prev.adminBadge
              return (
                <Fragment key={it.id}>
                  {showDivider && <div className="my-2 mx-3 h-px bg-[var(--line)]" aria-hidden="true" />}
                  <NavItem item={it} active={activeId === it.id} onItemClick={onItemClick} />
                </Fragment>
              )
            })}
          </nav>
        </div>
      )}

      <div className="flex-1" />

      {plan && (
        <div className="px-3 pb-3">
          <div className="rounded-card border border-[var(--line)] p-3 bg-[var(--bg-2)]">
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow">Tu plan</div>
              {plan.nombre && <Pill tone="purple">{plan.nombre}</Pill>}
            </div>
            {plan.usados != null && plan.total != null && (
              <>
                <div className="text-[11.5px] text-[var(--ink-3)] mb-2 tnum">
                  {plan.usados} / {plan.total} empleados
                </div>
                <div className="h-1.5 rounded-pill bg-[var(--line)] overflow-hidden">
                  <div
                    className="h-full ia-grad-bg"
                    style={{ width: `${Math.min(100, Math.round(plan.usados / plan.total * 100))}%` }}
                  />
                </div>
              </>
            )}
            {plan.cta && (
              <Btn size="sm" variant="ghost" className="mt-3 w-full justify-center" onClick={plan.cta.onClick}>
                {plan.cta.label}
              </Btn>
            )}
          </div>
        </div>
      )}

      <Hr />
      {userActions ? (
        <div className="p-3">{userActions}</div>
      ) : user && (
        <div className="p-3 flex items-center gap-2.5">
          <Avatar name={user.name} size={28} />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium truncate">{user.name}</div>
            {user.role && (
              <div className="text-[10.5px] font-mono text-[var(--ink-3)] truncate">
                {user.role}{user.email && ` · ${user.email}`}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}

function SidebarCollapsed({ navPrimary, navSecondary, activeId, onItemClick, onExpand }) {
  return (
    <aside
      className="w-[64px] shrink-0 border-r border-[var(--line)] flex flex-col items-center overflow-y-auto"
      style={{ background: 'var(--bg)' }}
      aria-label="Navegación lateral colapsada"
    >
      <div className="h-14 flex items-center"><Logo size={22} mark={true} label={false} /></div>
      <button
        type="button"
        onClick={onExpand}
        className="w-10 h-8 grid place-items-center rounded-soft text-[var(--ink-3)] hover:bg-[var(--bg-2)] mb-2"
        aria-label="Expandir menú"
      >
        <FiChevronsRight size={14} />
      </button>
      {navPrimary.length > 0 && (
        <>
          <Hr className="w-8" />
          <div className="flex flex-col gap-1 mt-3">
            {navPrimary.map((it, i) => {
              const prev = navPrimary[i - 1]
              const showDivider = prev && !!it.adminBadge !== !!prev.adminBadge
              return (
                <Fragment key={it.id}>
                  {showDivider && <div className="my-1 mx-auto w-6 h-px bg-[var(--line)]" aria-hidden="true" />}
                  <CollapsedItem item={it} active={activeId === it.id} onItemClick={onItemClick} />
                </Fragment>
              )
            })}
          </div>
        </>
      )}
      {navSecondary.length > 0 && (
        <>
          <Hr className="w-8 my-3" />
          <div className="flex flex-col gap-1">
            {navSecondary.map((it, i) => {
              const prev = navSecondary[i - 1]
              const showDivider = prev && !!it.adminBadge !== !!prev.adminBadge
              return (
                <Fragment key={it.id}>
                  {showDivider && <div className="my-1 mx-auto w-6 h-px bg-[var(--line)]" aria-hidden="true" />}
                  <CollapsedItem item={it} active={activeId === it.id} onItemClick={onItemClick} />
                </Fragment>
              )
            })}
          </div>
        </>
      )}
    </aside>
  )
}

export default function AppShellV2({
  navPrimary = DEFAULT_NAV_PRIMARY,
  navSecondary = DEFAULT_NAV_SECONDARY,
  activeId,
  onNavigate,
  user = { name: 'Rosa Fernández', role: 'Admin', email: 'rosa@…' },
  plan = { nombre: 'Avanzado', usados: 18, total: 25 },
  topBanner,
  topActions,
  userActions,
  children,
}) {
  const [collapsed, setCollapsed] = useState(false)
  const [internalActive, setInternalActive] = useState(activeId || (navPrimary[0]?.id ?? 'dashboard'))
  const active = activeId ?? internalActive
  const handleItemClick = (id) => {
    if (id) {
      setInternalActive(id)
      onNavigate?.(id)
    }
  }

  return (
    // h-screen + overflow-hidden mantiene la sidebar fija; el scroll vive en <main>.
    <div className="h-screen flex flex-col bg-[var(--bg)] text-[var(--ink)] font-sans antialiased overflow-hidden">
      {topBanner}
      <div className="flex-1 flex min-h-0">
        {collapsed
          ? <SidebarCollapsed
              navPrimary={navPrimary}
              navSecondary={navSecondary}
              activeId={active}
              onItemClick={handleItemClick}
              onExpand={() => setCollapsed(false)}
            />
          : <SidebarExpanded
              navPrimary={navPrimary}
              navSecondary={navSecondary}
              activeId={active}
              onItemClick={handleItemClick}
              onCollapse={() => setCollapsed(true)}
              user={user}
              plan={plan}
              topActions={topActions}
              userActions={userActions}
            />
        }
        {/* Contenido centrado con max-w-7xl. La sidebar queda fija a la izquierda. */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-7xl mx-auto w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
